import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { editorToolbarPluginKey } from '@atlaskit/editor-plugin-toolbar/plugin-key';
import { ToolbarButton, ToolbarTooltip } from '@atlaskit/editor-toolbar';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';

import type { MalleableUiPlugin, RemixDisabledReason } from '../../malleableUiPluginType';
import { messages } from '../messages';
import { getRemixDisabledTooltip } from '../remix-disabled-tooltip';

const testId = 'editor-toolbar__remix-button';

interface RemixButtonProps {
	api: ExtractInjectionAPI<MalleableUiPlugin>;
	isDisabled?: boolean;
	isRemixHidden?: boolean;
	remixDisabledReason?: RemixDisabledReason;
}

export const RemixButton = ({
	api,
	isDisabled,
	isRemixHidden,
	remixDisabledReason,
}: RemixButtonProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const experienceTracker = useAIMateExperienceTracker();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { isRemixing, areAiEntryPointsDisabled } = useSharedPluginStateWithSelector(
		api,
		['malleableUi', 'aiExperience'],
		(states) => ({
			isRemixing:
				states.malleableUiState?.isRemixModalOpen ||
				false ||
				states.malleableUiState?.isThinking ||
				false,
			areAiEntryPointsDisabled: states.aiExperienceState?.areAiEntryPointsDisabled ?? false,
		}),
	);

	const effectiveDisabled = isDisabled || areAiEntryPointsDisabled;

	const handleClick = useCallback(() => {
		if (effectiveDisabled || isRemixing) {
			return;
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'remixModalFromToolbar',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'remix',
				attributes: {
					entryPoint: 'editor',
				},
			},
		}).fire();
		const openModal = api?.malleableUi.commands.openRemixModal({
			metadata: { interactionSource: 'remixModalFromToolbar' },
			source: 'selectionToolbar',
		});
		if (!openModal) {
			return;
		}
		experienceTracker.start({
			name: ExperienceName.REMIX_START,
			id: ExperienceName.REMIX_START,
			attributes: {
				entryPoint: 'editor',
				remixSource: 'selectionToolbar',
				product: 'confluence',
			},
		});
		api?.core.actions.execute(({ tr }) => {
			openModal({ tr });
			tr.setMeta(editorToolbarPluginKey, { shouldShowToolbar: false });
			return tr;
		});
	}, [api, effectiveDisabled, isRemixing, experienceTracker, createAnalyticsEvent]);

	// Evaluate the visibility gate first and bail out before touching cc_maui_create_keyword, so we
	// don't fire a cc_maui_create_keyword exposure for users who never see the Remix button.
	if (isRemixHidden || !expValEquals('cc-maui-experiment', 'isEnabled', true)) {
		return null;
	}

	// cc_maui_create_keyword: when enabled, swap the "Remix" keyword for "Create". Evaluated here
	// (after the visibility gate) so the exposure only fires when the button is actually rendered.
	const isCreateKeywordEnabled = expValEquals('cc_maui_create_keyword', 'isEnabled', true);

	let tooltipContent: string;
	if (isRemixing) {
		tooltipContent = formatMessage(
			isCreateKeywordEnabled ? messages.createInProgressTooltip : messages.remixInProgressTooltip,
		);
	} else if (effectiveDisabled) {
		tooltipContent = getRemixDisabledTooltip(formatMessage, remixDisabledReason);
	} else if (fg('cc_maui_updated_remix_tooltip_text')) {
		tooltipContent = formatMessage(
			isCreateKeywordEnabled
				? messages.createBlockTooltipUpdated
				: messages.remixToolbarTooltipUpdated,
		);
	} else {
		tooltipContent = formatMessage(
			isCreateKeywordEnabled ? messages.createToolbarTooltip : messages.remixToolbarTooltip,
		);
	}

	return (
		<ToolbarTooltip content={tooltipContent}>
			<ToolbarButton
				iconBefore={<AiGenerativeRemixIcon label="" size="small" />}
				testId={testId}
				onClick={handleClick}
				isDisabled={effectiveDisabled || isRemixing}
			>
				{formatMessage(
					isCreateKeywordEnabled ? messages.createToolbarLabel : messages.remixToolbarLabel,
				)}
			</ToolbarButton>
		</ToolbarTooltip>
	);
};
